"use client";

import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackClientEvent } from "@/lib/tracking/client";
import { trackTikTokClientEvent } from "@/lib/tracking/tiktokClient";
import { useInView } from "@/hooks/useInView";
import { ScrollReveal } from "@/components/landing/ScrollReveal";
import { LandingSectionHeader } from "@/components/landing/LandingSectionHeader";
import {
  SHOPIFY_CUSTOMER_PORTAL,
  WORKSHOP_STORE_URL,
  buildSubscribeUrl,
  pricingTiers as defaultPricingTiers,
  type PricingTier,
} from "@/components/landing/homepage-data";
import { planIncludes, type PlanTier } from "@/lib/authz/plans";
import { featureKeys, type FeatureKey } from "@/lib/authz/policy";
import { FEATURE_LABELS } from "@/components/admin/users/lib";
import { useAuth } from "@/components/auth/AuthProvider";
import { startSubscriptionCheckout } from "@/lib/billing/startSubscriptionCheckout";
import { SUBSCRIPTION_CHECKOUT_PLANS } from "@/lib/billing/subscriptionCheckout";
import { useLocalizedPricing } from "@/lib/billing/useLocalizedPricing";
import { formatMoney, deriveEffectiveMonthly } from "@/lib/billing/money";
import { resolveSubscriptionSummary } from "@/lib/billing/subscriptionStatus";
import { PricingComparisonMatrix } from "./PricingComparisonMatrix";
import { CheckoutProgressBar } from "./CheckoutProgressBar";
import { ChangePlanDialog } from "./ChangePlanDialog";

type BillingMode = "annual" | "monthly";

type CardCta =
  | { kind: "subscribe" }
  | { kind: "upgrade" }
  | { kind: "current" }
  | { kind: "switch" }
  | { kind: "included" };

function ctaForTier(cardTier: PlanTier, currentPlan: PlanTier | undefined): CardCta {
  if (currentPlan === undefined) return { kind: "subscribe" };
  if (cardTier === currentPlan) return { kind: "current" };
  // "Included" means the current plan genuinely grants everything this card
  // grants — decided by the feature-inclusion lattice, NOT by price rank. This
  // is what keeps a Server Developer subscriber from being told the Vehicle
  // Developer track (Paint Booth, UV Studio, Blinklab, EasyMeta, Handling
  // Diff) is included when it isn't — the two are parallel tracks.
  if (planIncludes(currentPlan, cardTier)) return { kind: "included" };
  // This card is a strict superset of the current plan → a real, additive
  // upgrade (e.g. Server Developer → Pro).
  if (planIncludes(cardTier, currentPlan)) return { kind: "upgrade" };
  // Neither plan subsumes the other (parallel tracks, e.g. Vehicle ↔ Server):
  // a lateral switch, not an upgrade or an included tier. Changing tracks stays
  // in the billing portal — never the in-app upgrade path.
  return { kind: "switch" };
}

function ctaLabel(cta: CardCta): string {
  switch (cta.kind) {
    case "subscribe": return "Subscribe";
    case "upgrade": return "Upgrade";
    case "current": return "Current Plan";
    case "switch": return "Switch to this plan";
    case "included": return "Included in your plan";
  }
}

export type PricingTierCardsProps = {
  /**
   * When set, the matching tier card renders as "Current Plan", higher tiers
   * show "Upgrade", lower tiers show "Included in your plan". Leave undefined
   * for the public landing page (every card is "Subscribe").
   */
  currentPlan?: PlanTier;
  /** Override the default Workshop Shopify URL if needed. */
  purchaseUrl?: string;
  /** Override the displayed tiers; defaults to the public catalog. */
  tiers?: PricingTier[];
  /** Optional title/eyebrow override. Pass `null` to hide the header entirely. */
  header?: { eyebrow: string; title: string; copy: string } | null;
  /** Section background. Defaults to the landing page's cream. */
  backgroundClassName?: string;
  /**
   * When true, subscribe/upgrade cards start the in-app Shopify checkout
   * (admin preview) instead of linking out to the store page. Default false
   * keeps the public flow (cards link to the store).
   */
  inAppCheckout?: boolean;
  /**
   * When true (and `currentPlan` is set), upgrade cards open the in-app
   * plan-change dialog instead of linking to the customer portal. The change
   * edits the user's EXISTING Seal subscription server-side — no second
   * checkout. Upgrades only; downgrades stay in the billing portal. Default
   * false keeps subscriber clicks routed to the portal.
   */
  inAppPlanChange?: boolean;
};

const DEFAULT_HEADER = {
  eyebrow: "Pricing",
  title: "Pick the toolkit that matches your workflow.",
  copy: "Annual saves you two months. Switch tiers anytime.",
} as const;

export function PricingTierCards({
  currentPlan,
  purchaseUrl = WORKSHOP_STORE_URL,
  tiers = defaultPricingTiers,
  header = DEFAULT_HEADER,
  backgroundClassName = "bg-[#f5f5f3] py-8 sm:py-14",
  inAppCheckout = false,
  inAppPlanChange = false,
}: PricingTierCardsProps) {
  const [billing, setBilling] = useState<BillingMode>("annual");
  const isAnnual = billing === "annual";
  const [sectionRef, isVisible] = useInView<HTMLElement>(0.05);
  const [pendingTier, setPendingTier] = useState<PlanTier | null>(null);
  const [planChangeTarget, setPlanChangeTarget] = useState<PricingTier | null>(null);

  const { user, billing: subscriptionBilling, refreshPermissions } = useAuth();
  const buyerEmail = user?.email ?? undefined;
  // An existing subscriber must NOT run the in-app cartCreate — it would create
  // a SECOND concurrent subscription (double-billing). Detect a real active
  // subscription so we can route those clicks to the customer portal instead.
  // (We don't gate on auth `loading`: during that window this is false, but a
  // signed-in subscriber is never shown these in-app cards then — HomePageSwitcher
  // renders null while loading, and /subscription renders null for free/loading
  // users — so there's no double-bill window to guard.)
  const hasActiveSubscription =
    Boolean(subscriptionBilling?.personalSubscriptionId) ||
    ((subscriptionBilling?.personalSubscriptionIds?.length ?? 0) > 0);

  // Localized (presentment-currency) prices, auto-detected server-side. Falls
  // back to the hardcoded USD strings on each tier (and a USD cart) if Shopify
  // can't price the buyer's country.
  const { pricing } = useLocalizedPricing();

  // In-app plan change (Seal item swap on the existing subscription). Not gated
  // on hasActiveSubscription: admins testing the flow have no mirrored sub id
  // (loadAccessContext skips Seal for them) — the server validates the real
  // subscription either way.
  const planChangeEnabled = Boolean(inAppPlanChange && currentPlan !== undefined);
  // The dialog quotes the target price at the SUBSCRIPTION's billing interval,
  // not whatever the monthly/annual toggle happens to show.
  const subscriptionSummary = planChangeEnabled
    ? resolveSubscriptionSummary(subscriptionBilling?.personalSubscriptionData ?? null)
    : null;
  const subscriptionBillingMode: BillingMode =
    subscriptionSummary?.billingLabel === "annually" ? "annual" : "monthly";

  const planChangePriceLine = (target: PricingTier | null): string | null => {
    if (!target) return null;
    const sub = pricing?.subscriptions[target.tier];
    if (subscriptionBillingMode === "annual") {
      const annualPrice = formatMoney(sub?.annual) ?? target.annual;
      return `New price: ${annualPrice} billed annually.`;
    }
    const monthlyPrice = formatMoney(sub?.monthly) ?? target.monthly;
    return `New price: ${monthlyPrice}/mo.`;
  };

  // Suppress a duplicate checkout_started for the identical tier+billing+cta
  // within a short window (rapid double-click / double-bound handler / StrictMode
  // double-invoke). Real intent never re-fires the same checkout in <1.5s.
  const lastCheckoutRef = useRef<{ key: string; at: number } | null>(null);

  // Upgrade target from a locked-tool CTA (?upgrade=<tier>&tool=<feature>).
  // Read on mount from window.location so the SSR'd landing page needs no
  // Suspense boundary; junk values are ignored.
  const [upgradeTarget, setUpgradeTarget] = useState<{ tier: PlanTier; tool: FeatureKey | null } | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tier = params.get("upgrade") as PlanTier | null;
    if (!tier || !tiers.some((t) => t.tier === tier)) return;
    const tool = params.get("tool") as FeatureKey | null;
    setUpgradeTarget({ tier, tool: tool && featureKeys.includes(tool) ? tool : null });
    // Only the initial URL matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paywallTrackedRef = useRef(false);
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!upgradeTarget) return;
    if (!paywallTrackedRef.current) {
      paywallTrackedRef.current = true;
      trackClientEvent("paywall_viewed", {
        tool: upgradeTarget.tool ?? "unknown",
        current_plan: currentPlan ?? "free",
        is_subscriber: currentPlan !== undefined && currentPlan !== "free",
      });
    }
    // Auto-open the confirm dialog (nothing is charged until the user confirms
    // there) only for a real upgrade; never start a checkout on their behalf.
    if (
      !autoOpenedRef.current &&
      planChangeEnabled &&
      ctaForTier(upgradeTarget.tier, currentPlan).kind === "upgrade"
    ) {
      const target = tiers.find((t) => t.tier === upgradeTarget.tier);
      if (target) {
        autoOpenedRef.current = true;
        setPlanChangeTarget(target);
      }
    }
  }, [upgradeTarget, currentPlan, planChangeEnabled, tiers]);

  useEffect(() => {
    if (!upgradeTarget) return;
    const timer = window.setTimeout(() => {
      document
        .getElementById(`pricing-tier-${upgradeTarget.tier}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start", inline: "center" });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [upgradeTarget]);

  // The tier to emphasize: the upgrade target, unless the viewer already has it.
  const focusTier =
    upgradeTarget && ctaForTier(upgradeTarget.tier, currentPlan).kind !== "current"
      ? upgradeTarget.tier
      : null;

  return (
    <section ref={sectionRef} className={backgroundClassName}>
      <CheckoutProgressBar active={pendingTier !== null} />
      <div className="lb-container">
        <div className={cn("grid gap-10", header && "lg:grid-cols-[1fr_auto] lg:items-end")}>
          {header ? (
            <ScrollReveal>
              <LandingSectionHeader
                eyebrow={header.eyebrow}
                title={header.title}
                copy={header.copy}
              />
            </ScrollReveal>
          ) : null}
          <div className="inline-flex rounded-full border border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-panel)/0.9)] p-1 shadow-sm">
            {(["monthly", "annual"] as BillingMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setBilling(mode)}
                className={cn(
                  "rounded-full px-5 py-3 text-base font-semibold capitalize tracking-[-0.02em] transition",
                  billing === mode
                    ? "bg-[hsl(var(--lb-ink))] text-[hsl(var(--lb-bg))]"
                    : "text-[hsl(var(--lb-muted))] hover:text-[hsl(var(--lb-ink))]",
                )}
              >
                {mode}
                {mode === "annual" ? (
                  <span className="ml-2 rounded-full bg-[hsl(var(--lb-clay))] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-white shadow-[0_4px_14px_hsl(var(--lb-clay)/0.35)]">
                    2 months free
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        <PricingComparisonMatrix className="mt-8" />

        <div id="pricing" className="scroll-mt-20" />
        {/* When a locked-tool CTA targets a tier the viewer doesn't already have,
            that card is the only one emphasized: "Most popular" and the
            current-plan glow step back so the upgrade direction is unambiguous. */}
        <div className="mt-10 -mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 sm:mx-0 sm:px-0 lg:grid lg:grid-cols-4 lg:overflow-visible lg:pb-0">
          {tiers.map((tier, index) => {
            const cta = ctaForTier(tier.tier, currentPlan);
            const isClickable =
              cta.kind === "subscribe" || cta.kind === "upgrade" || cta.kind === "switch";
            const isCurrent = cta.kind === "current";
            const isPending = pendingTier === tier.tier;

            // Localized prices for this tier, falling back to the hardcoded USD.
            const sub = pricing?.subscriptions[tier.tier];
            const monthlyPrice = formatMoney(sub?.monthly) ?? tier.monthly;
            const annualPrice = formatMoney(sub?.annual) ?? tier.annual;
            const effectiveMonthlyPrice = deriveEffectiveMonthly(sub?.annual) ?? tier.effectiveMonthly;

            const isUpgradeTarget = focusTier === tier.tier;
            const emphasized = focusTier ? isUpgradeTarget : isCurrent || tier.featured;
            const sharedClass = cn(
              "group flex min-h-[39rem] w-[85vw] max-w-[22rem] shrink-0 snap-start flex-col rounded-[1.75rem] border bg-[hsl(var(--lb-panel)/0.9)] p-7 text-[hsl(var(--lb-ink))] shadow-[0_20px_70px_rgba(0,0,0,0.2)] transition lg:w-auto lg:max-w-none lg:shrink",
              isClickable && "hover:-translate-y-1 hover:border-[hsl(var(--lb-clay)/0.55)] hover:shadow-[0_28px_95px_rgba(0,0,0,0.28)]",
              !isClickable && "cursor-default",
              !emphasized
                ? "border-[hsl(var(--lb-line))]"
                : isCurrent
                  ? "border-[hsl(var(--lb-clay)/0.85)] bg-[radial-gradient(circle_at_50%_0%,hsl(var(--lb-clay)/0.28),transparent_22rem),linear-gradient(180deg,hsl(var(--lb-panel)),hsl(var(--lb-panel-strong)))]"
                  : "border-[hsl(var(--lb-clay)/0.85)] bg-[radial-gradient(circle_at_50%_0%,hsl(var(--lb-clay)/0.22),transparent_22rem),linear-gradient(180deg,hsl(var(--lb-panel)),hsl(var(--lb-panel-strong)))]",
              isUpgradeTarget && "ring-2 ring-[hsl(var(--lb-clay))] ring-offset-2 ring-offset-[hsl(var(--lb-bg))]",
            );

            const inlineStyle = {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "none" : "translateY(28px)",
              transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${index * 90}ms, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${index * 90}ms`,
            } as const;

            const inner = (
              <>
                <span id={`pricing-tier-${tier.tier}`} className="scroll-mt-24" />
                {isUpgradeTarget && upgradeTarget?.tool ? (
                  <p className="mb-3 inline-flex w-fit items-center rounded-full bg-[hsl(var(--lb-clay))] px-3 py-1 text-xs font-semibold tracking-tight text-white">
                    Unlocks {FEATURE_LABELS[upgradeTarget.tool]}
                  </p>
                ) : null}
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <h3 className="text-[1.85rem] font-semibold leading-[1.05] tracking-[-0.06em] text-[hsl(var(--lb-clay))] sm:text-[2.05rem]">{tier.name}</h3>
                  {isCurrent ? (
                    <span className="shrink-0 rounded-full border border-[hsl(var(--lb-clay)/0.55)] bg-[hsl(var(--lb-clay)/0.22)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--lb-ink))]">
                      Current Plan
                    </span>
                  ) : tier.featured && !focusTier ? (
                    <span className="shrink-0 rounded-full border border-[hsl(var(--lb-clay)/0.45)] bg-[hsl(var(--lb-clay)/0.16)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--lb-ink))]">
                      Most Popular
                    </span>
                  ) : null}
                </div>
                <p className="mt-6 min-h-20 text-base leading-7 text-[hsl(var(--lb-muted))]">
                  {tier.subtitle}
                </p>

                <div className="mt-8">
                  <p className="whitespace-nowrap text-[2.8rem] font-semibold leading-none tracking-[-0.07em] text-[hsl(var(--lb-clay))] sm:text-[3.2rem] lg:text-[3.6rem]">
                    {isAnnual ? effectiveMonthlyPrice : monthlyPrice}
                    <span className="ml-1 text-base tracking-[-0.03em] text-[hsl(var(--lb-subtle))]">/mo</span>
                  </p>
                  {isAnnual ? (
                    <div className="mt-3 text-sm leading-6 text-[hsl(var(--lb-muted))]">
                      <p>
                        <span className="line-through">{monthlyPrice}/mo</span> billed annually as {annualPrice}
                      </p>
                      <p className="mt-1">2 months free.</p>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm leading-6 text-[hsl(var(--lb-muted))]">
                      Billed monthly. Switch whenever your workflow changes.
                    </p>
                  )}
                </div>

                <div className="mt-10 flex-1">
                  {tier.highlight ? (
                    <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--lb-clay)/0.45)] bg-[hsl(var(--lb-clay)/0.16)] px-3 py-1 text-xs font-semibold tracking-tight text-[hsl(var(--lb-ink))]">
                      {tier.highlight}
                    </p>
                  ) : null}
                  <p className="font-mono text-xs uppercase tracking-[0.16em] text-[hsl(var(--lb-subtle))]">Includes</p>
                  <ul className="mt-5 space-y-3">
                    {tier.includes.map((item) =>
                      tier.spotlight?.includes(item) ? (
                        <li
                          key={item}
                          className="-mx-3 flex items-start gap-3 rounded-lg border border-[hsl(var(--lb-clay)/0.55)] bg-gradient-to-r from-[hsl(var(--lb-clay)/0.28)] to-[hsl(var(--lb-clay)/0.06)] px-3 py-1.5 text-base font-semibold leading-7 text-[hsl(var(--lb-ink))] shadow-[0_0_18px_hsl(var(--lb-clay)/0.35)]"
                        >
                          <Sparkles className="mt-1 h-4 w-4 shrink-0 text-[hsl(var(--lb-clay))]" />
                          <span>{item}</span>
                        </li>
                      ) : (
                        <li key={item} className="flex items-start gap-3 text-base leading-7">
                          <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[hsl(var(--lb-clay))]" />
                          <span>{item}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                <div
                  className={cn(
                    "mt-10 rounded-xl px-5 py-4 text-center text-base font-semibold transition",
                    isClickable && "group-hover:-translate-y-0.5",
                    isCurrent && emphasized
                      ? "border border-[hsl(var(--lb-clay)/0.55)] bg-[hsl(var(--lb-clay)/0.18)] text-[hsl(var(--lb-ink))]"
                      : emphasized && isClickable
                        ? "bg-[hsl(var(--lb-clay))] text-white"
                        : isClickable
                          ? "border border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-bg-soft))] text-[hsl(var(--lb-ink))]"
                          : "border border-[hsl(var(--lb-line)/0.6)] bg-[hsl(var(--lb-bg-soft)/0.5)] text-[hsl(var(--lb-muted))]",
                  )}
                >
                  {isPending ? "Starting checkout…" : ctaLabel(cta)}
                </div>
              </>
            );

            if (!isClickable) {
              return (
                <div key={tier.name} style={inlineStyle} className={sharedClass} aria-disabled>
                  {inner}
                </div>
              );
            }

            const trackCtaClick = () => {
              trackClientEvent("pricing_cta_clicked", {
                tier: tier.tier,
                cta_kind: cta.kind,
                billing_mode: billing,
              });
              if (cta.kind === "upgrade") {
                trackClientEvent("upgrade_clicked", { to_plan: tier.tier });
              }
              // Dedupe rapid duplicate checkout_started fires for the same intent.
              const dedupeKey = `${tier.tier}:${billing}:${cta.kind}`;
              const now = Date.now();
              const last = lastCheckoutRef.current;
              if (last && last.key === dedupeKey && now - last.at < 1500) return;
              lastCheckoutRef.current = { key: dedupeKey, at: now };
              // Value the checkout funnel: attach the exact presentment price the
              // buyer sees for this tier+billing (falls back to undefined when
              // localized pricing hasn't resolved).
              const money = pricing?.subscriptions?.[tier.tier]?.[billing];
              trackClientEvent("checkout_started", {
                kind: "subscription",
                tier: tier.tier,
                billing_mode: billing,
                source: inAppCheckout ? "pricing_cards_inapp" : "pricing_cards",
                price_amount: money ? Number(money.amount) : undefined,
                currency: money?.currencyCode ?? pricing?.currencyCode ?? undefined,
                plan_id: SUBSCRIPTION_CHECKOUT_PLANS[tier.tier]?.sellingPlanByBilling?.[billing],
              });
              // TikTok InitiateCheckout conversion (client pixel). No server
              // dedup needed — the purchase completes off-app on Shopify, so the
              // paying conversion is fired server-side as Subscribe (authz.ts).
              // `contents` is TikTok's documented Events 2.0 shape and is what
              // value-based bidding reads; value/currency are omitted entirely
              // when localized pricing hasn't resolved, since a null value is
              // scored as a zero-value conversion rather than an unknown one.
              const checkoutValue = money ? Number(money.amount) : undefined;
              const checkoutCurrency = money?.currencyCode ?? pricing?.currencyCode ?? undefined;
              trackTikTokClientEvent("InitiateCheckout", {
                content_type: "product",
                content_id: tier.tier,
                contents: [
                  {
                    content_id: tier.tier,
                    content_type: "product",
                    content_name: `${tier.name} (${billing})`,
                    quantity: 1,
                    ...(checkoutValue !== undefined ? { price: checkoutValue } : {}),
                  },
                ],
                ...(checkoutValue !== undefined ? { value: checkoutValue } : {}),
                ...(checkoutCurrency ? { currency: checkoutCurrency } : {}),
              });
            };

            // Lateral switch between parallel tracks (e.g. Vehicle ↔ Server):
            // neither plan includes the other, so it's neither an upgrade nor an
            // included tier. Route to the customer portal to change tracks —
            // never the in-app upgrade path (charges-now + rotates tokens as if
            // additive) or a fresh checkout (would double-bill an existing sub).
            if (cta.kind === "switch") {
              return (
                <a
                  key={tier.name}
                  href={SHOPIFY_CUSTOMER_PORTAL}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={inlineStyle}
                  className={sharedClass}
                  onClick={trackCtaClick}
                >
                  {inner}
                </a>
              );
            }

            // In-app plan change: confirm dialog → server swaps the variant on
            // the EXISTING Seal subscription. No checkout, no second
            // subscription, so checkout_started/InitiateCheckout don't fire.
            if (planChangeEnabled && cta.kind === "upgrade") {
              return (
                <button
                  key={tier.name}
                  type="button"
                  style={inlineStyle}
                  className={cn(sharedClass, "text-left")}
                  onClick={() => {
                    trackClientEvent("pricing_cta_clicked", {
                      tier: tier.tier,
                      cta_kind: cta.kind,
                      billing_mode: subscriptionBillingMode,
                    });
                    if (cta.kind === "upgrade") {
                      trackClientEvent("upgrade_clicked", { from_plan: currentPlan, to_plan: tier.tier });
                    }
                    setPlanChangeTarget(tier);
                  }}
                >
                  {inner}
                </button>
              );
            }

            // Existing subscriber: route to the Shopify customer portal to change
            // their plan. Running the in-app checkout here would create a SECOND
            // concurrent subscription (double-billing), so we never do that.
            if (hasActiveSubscription) {
              return (
                <a
                  key={tier.name}
                  href={SHOPIFY_CUSTOMER_PORTAL}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={inlineStyle}
                  className={sharedClass}
                  onClick={trackCtaClick}
                >
                  {inner}
                </a>
              );
            }

            // Net-new buyer with in-app checkout: build a Shopify cart and
            // redirect. The signed-in user's email is forwarded so the resulting
            // subscription auto-links to their account.
            if (inAppCheckout) {
              return (
                <button
                  key={tier.name}
                  type="button"
                  style={inlineStyle}
                  className={cn(sharedClass, "text-left disabled:cursor-wait disabled:opacity-70")}
                  disabled={pendingTier !== null}
                  onClick={() => {
                    trackCtaClick();
                    void (async () => {
                      setPendingTier(tier.tier);
                      // Forward the displayed-price country so the subscription's
                      // currency matches what the buyer saw (else server geo-IP).
                      const started = await startSubscriptionCheckout(
                        tier.tier,
                        billing,
                        buyerEmail,
                        pricing?.countryCode ?? undefined,
                      );
                      // On success the page navigates away — keep the button in its
                      // loading state until then. Only reset if checkout didn't start.
                      if (!started) setPendingTier(null);
                    })();
                  }}
                >
                  {inner}
                </button>
              );
            }

            // Default public flow: link out to the Shopify store page.
            return (
              <a
                key={tier.name}
                href={buildSubscribeUrl(tier.tier, purchaseUrl)}
                target="_blank"
                rel="noreferrer noopener"
                style={inlineStyle}
                className={sharedClass}
                onClick={trackCtaClick}
              >
                {inner}
              </a>
            );
          })}
        </div>

        <p className="mx-auto mt-8 max-w-3xl text-center text-lg leading-7 tracking-[-0.03em] text-[hsl(var(--lb-muted))]">
          Cancel anytime from your Shopify account. Switch tiers whenever your workflow changes.
        </p>

        {inAppCheckout && !hasActiveSubscription && !buyerEmail ? (
          <p className="mx-auto mt-3 max-w-3xl text-center text-sm text-[hsl(var(--lb-muted))]">
            Check out with the same email you&rsquo;ll use for your Workshop account — that&rsquo;s how your tools unlock.
          </p>
        ) : null}
      </div>

      {planChangeEnabled && currentPlan !== undefined ? (
        <ChangePlanDialog
          targetTier={planChangeTarget?.tier ?? null}
          targetName={planChangeTarget?.name ?? ""}
          currentPlan={currentPlan}
          // Placeholder only while the server quote loads — and only when the
          // subscription's billing interval is actually known. Without a
          // summary the line would default to the monthly price, which is
          // flatly wrong for annual subscribers (and for admins, whose mirror
          // is always empty).
          priceLine={subscriptionSummary ? planChangePriceLine(planChangeTarget) : null}
          onClose={() => setPlanChangeTarget(null)}
          onChanged={refreshPermissions}
        />
      ) : null}
    </section>
  );
}
